import React, { useState, useEffect } from "react";
import axios from "axios";
import { useParams, useNavigate } from "react-router-dom";
import {
  FiArrowLeft, FiUpload, FiPlus, FiX,
  FiPhone, FiMail, FiGlobe, FiMessageSquare,
  FiMapPin, FiCheck,
} from "react-icons/fi";

const API_BASE = "https://estatebackend.pixelmindsolutions.com";

/* ─── Styles ─── */
const StyleTag = () => (
  <style>{`
    @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;600&family=DM+Sans:wght@300;400;500&display=swap');

    :root {
      --red-from:   #ef4444;
      --red-to:     #dc2626;
      --red-soft:   rgba(239,68,68,0.07);
      --red-border: rgba(239,68,68,0.18);
      --bg:         #f8f7f5;
      --surface:    #ffffff;
      --border:     #ede9e3;
      --ink:        #1a1410;
      --muted:      #8a8278;
      --shadow-sm:  0 1px 3px rgba(0,0,0,0.06),0 1px 2px rgba(0,0,0,0.04);
      --shadow-md:  0 4px 16px rgba(0,0,0,0.08),0 2px 6px rgba(0,0,0,0.04);
    }

    * { box-sizing: border-box; }

    .ch-root {
      min-height: 100vh;
      background: var(--bg);
      font-family: 'DM Sans', sans-serif;
      color: var(--ink);
      padding: 32px 16px 80px;
    }

    .ch-card {
      max-width: 860px;
      margin: 0 auto;
      background: var(--surface);
      border: 1px solid var(--border);
      border-radius: 24px;
      box-shadow: var(--shadow-sm);
      overflow: hidden;
    }

    /* ── Header ── */
    .ch-header {
      display: flex;
      align-items: center;
      gap: 14px;
      padding: 22px 28px;
      border-bottom: 1px solid var(--border);
      position: relative;
    }
    .ch-header::before {
      content: '';
      position: absolute;
      top: 0; left: 0; right: 0;
      height: 3px;
      background: linear-gradient(90deg, var(--red-from), var(--red-to));
    }
    .ch-back-btn {
      width: 36px; height: 36px;
      border-radius: 10px;
      border: 1px solid var(--border);
      background: transparent;
      display: flex; align-items: center; justify-content: center;
      color: var(--muted);
      cursor: pointer;
      transition: background 0.15s, border-color 0.15s, color 0.15s;
      flex-shrink: 0;
    }
    .ch-back-btn:hover { background: var(--red-soft); border-color: var(--red-border); color: var(--red-from); }
    .ch-header-eyebrow {
      font-size: 10px;
      font-weight: 500;
      letter-spacing: 0.16em;
      text-transform: uppercase;
      background: linear-gradient(135deg, var(--red-from), var(--red-to));
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      background-clip: text;
      margin-bottom: 4px;
    }
    .ch-header-title {
      font-family: 'Playfair Display', serif;
      font-size: 1.4rem;
      font-weight: 600;
      line-height: 1;
      color: var(--ink);
    }

    /* ── Category label badge (dynamic) ── */
    .ch-selected-label {
      display: flex;
      align-items: center;
      gap: 8px;
      margin-left: auto;
      padding: 6px 12px 6px 8px;
      border-radius: 100px;
      background: var(--red-soft);
      border: 1px solid var(--red-border);
      font-size: 12px;
      font-weight: 500;
      color: var(--red-to);
      transition: opacity 0.2s;
    }
    .ch-selected-label img {
      width: 22px; height: 22px;
      border-radius: 6px;
      object-fit: cover;
      border: 1px solid var(--red-border);
    }

    /* ── Form body ── */
    .ch-form { padding: 28px; }

    /* ── Section ── */
    .ch-section { margin-bottom: 32px; }
    .ch-section-header {
      display: flex;
      align-items: center;
      gap: 10px;
      margin-bottom: 18px;
    }
    .ch-section-icon {
      width: 28px; height: 28px;
      border-radius: 8px;
      display: flex; align-items: center; justify-content: center;
      flex-shrink: 0;
    }
    .ch-section-title {
      font-size: 11px;
      font-weight: 600;
      letter-spacing: 0.14em;
      text-transform: uppercase;
      color: var(--muted);
    }
    .ch-section-line { flex: 1; height: 1px; background: var(--border); }

    /* ── Category card grid ── */
    .cat-grid {
      display: grid;
      grid-template-columns: repeat(auto-fill, minmax(100px, 1fr));
      gap: 10px;
    }
    .cat-card {
      border: 2px solid var(--border);
      border-radius: 14px;
      overflow: hidden;
      cursor: pointer;
      transition: border-color 0.18s, transform 0.18s, box-shadow 0.18s;
      background: var(--bg);
      position: relative;
    }
    .cat-card:hover {
      border-color: var(--red-border);
      transform: translateY(-2px);
      box-shadow: var(--shadow-md);
    }
    .cat-card.selected {
      border-color: var(--red-from);
      box-shadow: 0 0 0 3px rgba(239,68,68,0.12);
    }
    .cat-card-img {
      width: 100%; height: 64px;
      object-fit: cover;
      display: block;
      background: #eae5de;
    }
    .cat-card-img-fallback {
      width: 100%; height: 64px;
      background: var(--red-soft);
      display: flex; align-items: center; justify-content: center;
      font-family: 'Playfair Display', serif;
      font-size: 1.2rem; color: var(--red-from);
    }
    .cat-card-label {
      padding: 6px 8px;
      font-size: 11px;
      font-weight: 500;
      color: var(--ink);
      text-align: center;
      line-height: 1.3;
    }
    .cat-check {
      position: absolute;
      top: 5px; right: 5px;
      width: 18px; height: 18px;
      border-radius: 50%;
      background: linear-gradient(135deg, var(--red-from), var(--red-to));
      display: flex; align-items: center; justify-content: center;
      color: #fff;
      opacity: 0;
      transform: scale(0.6);
      transition: opacity 0.15s, transform 0.15s;
    }
    .cat-card.selected .cat-check { opacity: 1; transform: scale(1); }

    /* ── Inputs ── */
    .ch-field { margin-bottom: 16px; }
    .ch-label {
      display: block;
      font-size: 12px;
      font-weight: 500;
      letter-spacing: 0.04em;
      color: var(--muted);
      margin-bottom: 6px;
      text-transform: uppercase;
    }
    .ch-input {
      width: 100%;
      padding: 11px 14px;
      border-radius: 12px;
      border: 1px solid var(--border);
      background: var(--bg);
      font-family: 'DM Sans', sans-serif;
      font-size: 13px;
      color: var(--ink);
      outline: none;
      transition: border-color 0.15s, box-shadow 0.15s;
    }
    .ch-input:focus {
      border-color: var(--red-from);
      box-shadow: 0 0 0 3px rgba(239,68,68,0.1);
    }
    .ch-input::placeholder { color: #c0b9b1; }
    .ch-input.has-icon { padding-left: 38px; }
    .ch-input-wrap { position: relative; }
    .ch-input-icon {
      position: absolute;
      left: 12px; top: 50%;
      transform: translateY(-50%);
      color: var(--muted);
      pointer-events: none;
    }
    .ch-textarea {
      resize: vertical;
      min-height: 100px;
      line-height: 1.6;
    }
    .ch-error { font-size: 11px; color: var(--red-from); margin-top: 4px; }

    .ch-grid-2 {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 14px;
    }
    @media (max-width: 560px) {
      .ch-grid-2 { grid-template-columns: 1fr; }
      .cat-grid   { grid-template-columns: repeat(auto-fill, minmax(85px, 1fr)); }
    }

    /* ── Attribute / Feature rows ── */
    .kv-row {
      display: flex;
      align-items: center;
      gap: 8px;
      margin-bottom: 8px;
    }
    .kv-row .ch-input { margin-bottom: 0; }
    .btn-remove {
      width: 34px; height: 34px;
      border-radius: 9px;
      border: 1px solid var(--red-border);
      background: var(--red-soft);
      color: var(--red-from);
      display: flex; align-items: center; justify-content: center;
      cursor: pointer;
      flex-shrink: 0;
      transition: background 0.12s;
    }
    .btn-remove:hover { background: rgba(239,68,68,0.13); }
    .btn-add-row {
      display: inline-flex;
      align-items: center;
      gap: 5px;
      margin-top: 8px;
      padding: 7px 14px;
      border-radius: 9px;
      border: 1px dashed var(--red-border);
      background: transparent;
      color: var(--red-to);
      font-family: 'DM Sans', sans-serif;
      font-size: 12px;
      font-weight: 500;
      cursor: pointer;
      transition: background 0.12s;
    }
    .btn-add-row:hover { background: var(--red-soft); }

    /* ── Upload ── */
    .upload-zone {
      width: 100%;
      border: 1.5px dashed var(--red-border);
      border-radius: 16px;
      padding: 32px 20px;
      background: var(--red-soft);
      display: flex; flex-direction: column;
      align-items: center; justify-content: center;
      cursor: pointer;
      transition: border-color 0.15s, background 0.15s;
      text-align: center;
      position: relative;
    }
    .upload-zone:hover { border-color: var(--red-from); background: rgba(239,68,68,0.1); }
    .upload-zone input[type="file"] {
      position: absolute; inset: 0;
      opacity: 0; cursor: pointer;
      width: 100%; height: 100%;
    }
    .upload-icon {
      width: 40px; height: 40px;
      border-radius: 11px;
      background: linear-gradient(135deg, var(--red-from), var(--red-to));
      display: flex; align-items: center; justify-content: center;
      color: #fff;
      margin-bottom: 10px;
      box-shadow: 0 4px 12px rgba(239,68,68,0.28);
    }
    .upload-label { font-size: 13px; font-weight: 500; color: var(--ink); }
    .upload-sub   { font-size: 11px; color: var(--muted); margin-top: 3px; }

    .preview-grid { display: flex; flex-wrap: wrap; gap: 10px; margin-bottom: 10px; }
    .preview-item { position: relative; }
    .preview-img {
      width: 84px; height: 84px;
      border-radius: 12px;
      object-fit: cover;
      border: 1px solid var(--border);
      display: block;
    }
    .preview-remove {
      position: absolute;
      top: -6px; right: -6px;
      width: 20px; height: 20px;
      border-radius: 50%;
      background: linear-gradient(135deg, var(--red-from), var(--red-to));
      color: #fff;
      border: 2px solid #fff;
      display: flex; align-items: center; justify-content: center;
      cursor: pointer;
      opacity: 0;
      transition: opacity 0.15s;
    }
    .preview-item:hover .preview-remove { opacity: 1; }
    .preview-add {
      width: 84px; height: 84px;
      border-radius: 12px;
      border: 1.5px dashed var(--red-border);
      background: var(--red-soft);
      display: flex; flex-direction: column;
      align-items: center; justify-content: center;
      cursor: pointer;
      color: var(--red-from);
      font-size: 10px;
      gap: 4px;
      position: relative;
      transition: background 0.12s;
    }
    .preview-add:hover { background: rgba(239,68,68,0.12); }
    .preview-add input { position: absolute; inset: 0; opacity: 0; cursor: pointer; width: 100%; height: 100%; }

    /* ── Actions ── */
    .ch-actions {
      display: flex;
      justify-content: flex-end;
      gap: 10px;
      padding: 20px 28px;
      border-top: 1px solid var(--border);
      flex-wrap: wrap;
    }
    .btn-cancel {
      padding: 11px 22px;
      border-radius: 12px;
      border: 1px solid var(--border);
      background: transparent;
      font-family: 'DM Sans', sans-serif;
      font-size: 13px;
      font-weight: 500;
      color: var(--muted);
      cursor: pointer;
      transition: border-color 0.15s, color 0.15s, background 0.15s;
    }
    .btn-cancel:hover { border-color: #ccc; color: var(--ink); background: #f3f0ec; }
    .btn-submit {
      display: flex; align-items: center; gap: 8px;
      padding: 11px 28px;
      border-radius: 12px;
      border: none;
      background: linear-gradient(135deg, var(--red-from), var(--red-to));
      color: #fff;
      font-family: 'DM Sans', sans-serif;
      font-size: 13px;
      font-weight: 500;
      cursor: pointer;
      transition: opacity 0.15s, transform 0.12s, box-shadow 0.15s;
      box-shadow: 0 4px 14px rgba(239,68,68,0.3);
    }
    .btn-submit:hover:not(:disabled) {
      transform: translateY(-1px);
      box-shadow: 0 6px 20px rgba(239,68,68,0.4);
    }
    .btn-submit:disabled { opacity: 0.55; cursor: not-allowed; }

    @keyframes chspin { to { transform: rotate(360deg); } }
    .ch-spinner {
      width: 14px; height: 14px;
      border: 2px solid rgba(255,255,255,0.3);
      border-top-color: #fff;
      border-radius: 50%;
      animation: chspin 0.7s linear infinite;
    }

    @media (max-width: 600px) {
      .ch-form { padding: 20px 16px; }
      .ch-header { padding: 18px 16px; }
      .ch-actions { padding: 16px; }
    }
  `}</style>
);

/* ─── Section wrapper ─── */
const Section = ({ title, icon, iconBg, iconColor, children }) => (
  <div className="ch-section">
    <div className="ch-section-header">
      <div className="ch-section-icon" style={{ background: iconBg, color: iconColor }}>
        {icon}
      </div>
      <span className="ch-section-title">{title}</span>
      <div className="ch-section-line" />
    </div>
    {children}
  </div>
);

/* ─── Field wrapper ─── */
const Field = ({ label, error, children }) => (
  <div className="ch-field">
    {label && <label className="ch-label">{label}</label>}
    {children}
    {error && <p className="ch-error">{error}</p>}
  </div>
);

/* ─── Upload zone ─── */
const UploadZone = ({ multiple, onChange, label }) => (
  <div className="upload-zone">
    <input type="file" multiple={multiple} accept="image/*" onChange={(e) => onChange(e.target.files)} />
    <div className="upload-icon"><FiUpload size={18} /></div>
    <div className="upload-label">{label || `Click or drop to upload`}</div>
    <div className="upload-sub">PNG · JPG · WEBP</div>
  </div>
);

/* ══════════════════════════════════════════
   MAIN COMPONENT
══════════════════════════════════════════ */
const CreateHouse = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const adminData = JSON.parse(sessionStorage.getItem("AdminData") || "{}");

  /* ── Categories ── */
  const [categories, setCategories]           = useState([]);
  const [selectedCategoryId, setSelectedCategoryId] = useState(id || "");

  /* ── Basic ── */
  const [name, setName]             = useState("");
  const [address, setAddress]       = useState("");
  const [description, setDescription] = useState("");
  const [latitude, setLatitude]     = useState("");
  const [longitude, setLongitude]   = useState("");

  /* ── Dynamic fields ── */
  const [attributes, setAttributes] = useState([{ key: "", value: "" }]);
  const [featureNames, setFeatureNames] = useState([""]);

  /* ── Contact ── */
  const [contact, setContact] = useState({ callNumber: "", whatsappNumber: "", email: "", website: "" });

  /* ── Images ── */
  const [images, setImages]             = useState([]);
  const [featureImages, setFeatureImages] = useState([]);
  const [imagePreviews, setImagePreviews]   = useState([]);
  const [featurePreviews, setFeaturePreviews] = useState([]);

  const [loading, setLoading] = useState(false);
  const [errors, setErrors]   = useState({});

  /* ── Fetch categories ── */
  useEffect(() => {
    axios.get(`${API_BASE}/api/auth/getall-categories`)
      .then((res) => setCategories(res.data.categories || []))
      .catch(() => {});
  }, []);

  /* ── Selected category object ── */
  const selectedCategory = categories.find((c) => c._id === selectedCategoryId);

  /* ── Image helpers ── */
  const handleImages = (files) => {
    const arr = Array.from(files);
    setImages(arr);
    setImagePreviews(arr.map((f) => URL.createObjectURL(f)));
  };
  const handleFeatureImages = (files) => {
    const arr = Array.from(files);
    setFeatureImages(arr);
    setFeaturePreviews(arr.map((f) => URL.createObjectURL(f)));
  };
  const removeImage = (i) => {
    setImages((p) => p.filter((_, idx) => idx !== i));
    setImagePreviews((p) => p.filter((_, idx) => idx !== i));
  };
  const removeFeatureImage = (i) => {
    setFeatureImages((p) => p.filter((_, idx) => idx !== i));
    setFeaturePreviews((p) => p.filter((_, idx) => idx !== i));
  };

  /* ── Feature handlers ── */
  const addFeature    = () => setFeatureNames([...featureNames, ""]);
  const removeFeature = (i) => setFeatureNames(featureNames.filter((_, idx) => idx !== i));
  const updateFeature = (i, val) => { const u = [...featureNames]; u[i] = val; setFeatureNames(u); };

  /* ── Attribute handlers ── */
  const addAttribute    = () => setAttributes([...attributes, { key: "", value: "" }]);
  const removeAttribute = (i) => setAttributes(attributes.filter((_, idx) => idx !== i));
  const updateAttribute = (i, field, val) => { const u = [...attributes]; u[i][field] = val; setAttributes(u); };

  /* ── Validate ── */
  const validate = () => {
    const e = {};
    if (!name.trim())          e.name      = "Name is required";
    if (!address.trim())       e.address   = "Address is required";
    if (!latitude)             e.latitude  = "Latitude is required";
    if (!longitude)            e.longitude = "Longitude is required";
    if (!selectedCategoryId)   e.category  = "Please select a category";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  /* ── Submit ── */
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    try {
      setLoading(true);
      const attributesObj = {};
      attributes.forEach(({ key, value }) => {
        if (key.trim()) {
          const parsed = value === "true" ? true : value === "false" ? false : isNaN(value) ? value : Number(value);
          attributesObj[key.trim()] = parsed;
        }
      });
      const contactObj = {};
      Object.entries(contact).forEach(([k, v]) => { if (v.trim()) contactObj[k] = v.trim(); });

      const fd = new FormData();
      fd.append("userId", adminData._id || adminData.id || "");
      fd.append("name", name.trim());
      fd.append("address", address.trim());
      fd.append("description", description.trim());
      fd.append("latitude", latitude);
      fd.append("longitude", longitude);
      fd.append("attributes", JSON.stringify(attributesObj));
      fd.append("featureNames", JSON.stringify(featureNames.filter((f) => f.trim())));
      if (Object.keys(contactObj).length > 0) fd.append("contact", JSON.stringify(contactObj));
      images.forEach((img) => fd.append("images", img));
      featureImages.forEach((img) => fd.append("featureImages", img));

      await axios.post(`${API_BASE}/api/create/${selectedCategoryId}`, fd);
      navigate(-1);
    } catch (err) {
      alert(err?.response?.data?.message || "Failed to create listing");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <StyleTag />
      <div className="ch-root">
        <div className="ch-card">

          {/* HEADER */}
          <div className="ch-header">
            <button className="ch-back-btn" onClick={() => navigate(-1)}>
              <FiArrowLeft size={15} />
            </button>
            <div>
              <div className="ch-header-eyebrow">Admin · Listings</div>
              <div className="ch-header-title">Create Listing</div>
            </div>

            {/* Dynamic label based on selected category */}
            {selectedCategory && (
              <div className="ch-selected-label">
                {selectedCategory.image && (
                  <img src={selectedCategory.image} alt={selectedCategory.name} />
                )}
                {selectedCategory.name}
              </div>
            )}
          </div>

          <form onSubmit={handleSubmit}>
            <div className="ch-form">

              {/* ── CATEGORY CARD GRID ── */}
              <Section
                title="Select Category"
                icon={<span style={{ fontSize: 13 }}>◈</span>}
                iconBg="var(--red-soft)"
                iconColor="var(--red-from)"
              >
                {errors.category && <p className="ch-error" style={{ marginBottom: 10 }}>{errors.category}</p>}
                <div className="cat-grid">
                  {categories.map((cat) => (
                    <div
                      key={cat._id}
                      className={`cat-card ${selectedCategoryId === cat._id ? "selected" : ""}`}
                      onClick={() => setSelectedCategoryId(cat._id)}
                    >
                      {cat.image ? (
                        <img className="cat-card-img" src={cat.image} alt={cat.name}
                          onError={(e) => { e.target.style.display = "none"; }} />
                      ) : (
                        <div className="cat-card-img-fallback">{cat.name.charAt(0)}</div>
                      )}
                      <div className="cat-card-label">{cat.name}</div>
                      <div className="cat-check"><FiCheck size={10} /></div>
                    </div>
                  ))}
                </div>
              </Section>

              {/* ── BASIC INFO ── */}
              <Section
                title="Basic Information"
                icon={<span style={{ fontSize: 12 }}>✦</span>}
                iconBg="rgba(37,99,235,0.08)"
                iconColor="#2563eb"
              >
                <div className="ch-grid-2">
                  <Field label="Name *" error={errors.name}>
                    <input className="ch-input" placeholder="e.g. Luxury Resort" value={name}
                      onChange={(e) => setName(e.target.value)} />
                  </Field>
                  <Field label="Address *" error={errors.address}>
                    <input className="ch-input" placeholder="e.g. Hyderabad, Telangana" value={address}
                      onChange={(e) => setAddress(e.target.value)} />
                  </Field>
                </div>
                <Field label="Description">
                  <textarea className="ch-input ch-textarea" placeholder="Describe the listing…"
                    value={description} onChange={(e) => setDescription(e.target.value)} />
                </Field>
              </Section>

              {/* ── LOCATION ── */}
              <Section
                title="Location Coordinates"
                icon={<FiMapPin size={12} />}
                iconBg="rgba(5,150,105,0.08)"
                iconColor="#059669"
              >
                <div className="ch-grid-2">
                  <Field label="Latitude *" error={errors.latitude}>
                    <input className="ch-input" placeholder="e.g. 17.3850" value={latitude}
                      onChange={(e) => setLatitude(e.target.value)} />
                  </Field>
                  <Field label="Longitude *" error={errors.longitude}>
                    <input className="ch-input" placeholder="e.g. 78.4867" value={longitude}
                      onChange={(e) => setLongitude(e.target.value)} />
                  </Field>
                </div>
              </Section>

              {/* ── ATTRIBUTES ── */}
              <Section
                title="Attributes"
                icon={<span style={{ fontSize: 12 }}>⊞</span>}
                iconBg="rgba(124,58,237,0.08)"
                iconColor="#7c3aed"
              >
                <p style={{ fontSize: 12, color: "var(--muted)", marginBottom: 12, lineHeight: 1.5 }}>
                  Add key-value pairs for this listing (e.g. <em>rooms → 10</em>, <em>wifi → true</em>, <em>price → 5000</em>)
                </p>
                {attributes.map((attr, i) => (
                  <div className="kv-row" key={i}>
                    <input
                      className="ch-input"
                      placeholder="Key (e.g. rooms)"
                      value={attr.key}
                      onChange={(e) => updateAttribute(i, "key", e.target.value)}
                    />
                    <input
                      className="ch-input"
                      placeholder="Value (e.g. 10)"
                      value={attr.value}
                      onChange={(e) => updateAttribute(i, "value", e.target.value)}
                    />
                    {attributes.length > 1 && (
                      <button type="button" className="btn-remove" onClick={() => removeAttribute(i)}>
                        <FiX size={13} />
                      </button>
                    )}
                  </div>
                ))}
                <button type="button" className="btn-add-row" onClick={addAttribute}>
                  <FiPlus size={12} /> Add Attribute
                </button>
              </Section>

              {/* ── FEATURES ── */}
              <Section
                title="Features"
                icon={<span style={{ fontSize: 12 }}>◇</span>}
                iconBg="rgba(8,145,178,0.08)"
                iconColor="#0891b2"
              >
                {featureNames.map((f, i) => (
                  <div className="kv-row" key={i}>
                    <input
                      className="ch-input"
                      placeholder="e.g. Swimming Pool"
                      value={f}
                      onChange={(e) => updateFeature(i, e.target.value)}
                    />
                    {featureNames.length > 1 && (
                      <button type="button" className="btn-remove" onClick={() => removeFeature(i)}>
                        <FiX size={13} />
                      </button>
                    )}
                  </div>
                ))}
                <button type="button" className="btn-add-row" onClick={addFeature}>
                  <FiPlus size={12} /> Add Feature
                </button>
              </Section>

              {/* ── CONTACT ── */}
              <Section
                title="Contact Information"
                icon={<FiPhone size={12} />}
                iconBg="rgba(217,119,6,0.08)"
                iconColor="#d97706"
              >
                <div className="ch-grid-2">
                  <Field label="Call Number">
                    <div className="ch-input-wrap">
                      <FiPhone size={13} className="ch-input-icon" />
                      <input className="ch-input has-icon" placeholder="9876543210"
                        value={contact.callNumber} onChange={(e) => setContact({ ...contact, callNumber: e.target.value })} />
                    </div>
                  </Field>
                  <Field label="WhatsApp Number">
                    <div className="ch-input-wrap">
                      <FiMessageSquare size={13} className="ch-input-icon" />
                      <input className="ch-input has-icon" placeholder="9876543210"
                        value={contact.whatsappNumber} onChange={(e) => setContact({ ...contact, whatsappNumber: e.target.value })} />
                    </div>
                  </Field>
                  <Field label="Email">
                    <div className="ch-input-wrap">
                      <FiMail size={13} className="ch-input-icon" />
                      <input className="ch-input has-icon" placeholder="hello@example.com" type="email"
                        value={contact.email} onChange={(e) => setContact({ ...contact, email: e.target.value })} />
                    </div>
                  </Field>
                  <Field label="Website">
                    <div className="ch-input-wrap">
                      <FiGlobe size={13} className="ch-input-icon" />
                      <input className="ch-input has-icon" placeholder="https://example.com"
                        value={contact.website} onChange={(e) => setContact({ ...contact, website: e.target.value })} />
                    </div>
                  </Field>
                </div>
              </Section>

              {/* ── LISTING IMAGES ── */}
              <Section
                title="Listing Images"
                icon={<FiUpload size={12} />}
                iconBg="rgba(239,68,68,0.08)"
                iconColor="var(--red-from)"
              >
                {imagePreviews.length > 0 ? (
                  <>
                    <div className="preview-grid">
                      {imagePreviews.map((src, i) => (
                        <div className="preview-item" key={i}>
                          <img src={src} alt="" className="preview-img" />
                          <button type="button" className="preview-remove" onClick={() => removeImage(i)}>
                            <FiX size={9} />
                          </button>
                        </div>
                      ))}
                      <label className="preview-add">
                        <input type="file" multiple accept="image/*" onChange={(e) => handleImages(e.target.files)} />
                        <FiPlus size={16} />
                        <span>More</span>
                      </label>
                    </div>
                    <p style={{ fontSize: 11, color: "var(--muted)" }}>
                      {images.length} image{images.length !== 1 ? "s" : ""} selected
                    </p>
                  </>
                ) : (
                  <UploadZone multiple onChange={handleImages} label="Click or drop listing images" />
                )}
              </Section>

              {/* ── FEATURE ICONS ── */}
              <Section
                title="Feature Icons"
                icon={<span style={{ fontSize: 12 }}>⬡</span>}
                iconBg="rgba(22,163,74,0.08)"
                iconColor="#15803d"
              >
                {featurePreviews.length > 0 ? (
                  <>
                    <div className="preview-grid">
                      {featurePreviews.map((src, i) => (
                        <div className="preview-item" key={i}>
                          <img src={src} alt="" className="preview-img" />
                          <button type="button" className="preview-remove" onClick={() => removeFeatureImage(i)}>
                            <FiX size={9} />
                          </button>
                        </div>
                      ))}
                      <label className="preview-add">
                        <input type="file" multiple accept="image/*" onChange={(e) => handleFeatureImages(e.target.files)} />
                        <FiPlus size={16} />
                        <span>More</span>
                      </label>
                    </div>
                    <p style={{ fontSize: 11, color: "var(--muted)" }}>
                      {featureImages.length} icon{featureImages.length !== 1 ? "s" : ""} selected
                    </p>
                  </>
                ) : (
                  <UploadZone multiple onChange={handleFeatureImages} label="Upload feature icons" />
                )}
              </Section>

            </div>

            {/* ── ACTIONS ── */}
            <div className="ch-actions">
              <button type="button" className="btn-cancel" onClick={() => navigate(-1)}>
                Cancel
              </button>
              <button type="submit" className="btn-submit" disabled={loading}>
                {loading && <span className="ch-spinner" />}
                {loading ? "Creating…" : "Create Listing"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

export default CreateHouse;